import * as React from 'react';
import { isArray } from 'util';

export enum TabPanelAlignment {
    Horizontal,
    Vertical,
}

interface ITabPanelProps {
    children: Array<JSX.Element> | JSX.Element;
    onChange?: (tabName: string) => void;
    tabIndex?: number;
    initialTabIndex?: number;
    alignment?: TabPanelAlignment;
}

interface ITabPanelState {
    currentTab: string;
    hasFocus: boolean;
    tabs: Array<string>;
}

class TabPanel extends React.Component<ITabPanelProps, ITabPanelState> {
    private tabArray: Array<JSX.Element>;

    constructor(props: ITabPanelProps) {
        super(props);

        this.state = {
            tabs: [],
            currentTab: '',
            hasFocus: false,
        };
    }

    componentWillMount() {
        this.tabArray = this.getChildTabs();
        if (this.tabArray && this.tabArray.length > 0) {
            this.setState({
                currentTab: this.tabArray[this.props.initialTabIndex || 0].props.tabName,
                tabs: this.tabArray.map((tab: JSX.Element) => tab.props.tabName),
            });
        }
    }

    componentWillReceiveProps(props: ITabPanelProps) {
        if (props.initialTabIndex !== this.props.initialTabIndex) {
            this.setState({ currentTab: this.tabArray[props.initialTabIndex].props.tabName });
        }
    }

    getChildTabs = () => {
        return (isArray(this.props.children) ? this.props.children : [this.props.children]) as Array<JSX.Element>;
    };

    updateTab = (tabName: string) => {
        if (this.state.currentTab !== tabName) {
            this.setState({ currentTab: tabName });
            if (this.props.onChange) {
                this.props.onChange(tabName);
            }
        }
    };

    keydownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (this.props.alignment === undefined || this.props.alignment === TabPanelAlignment.Vertical) {
            if (e.key === 'ArrowDown') {
                const currentIndex = this.state.tabs.indexOf(this.state.currentTab);
                if (this.state.currentTab !== this.state.tabs[this.state.tabs.length - 1]) {
                    this.updateTab(this.state.tabs[currentIndex + 1]);
                }
            } else if (e.key === 'ArrowUp') {
                const currentIndex = this.state.tabs.indexOf(this.state.currentTab);
                if (this.state.currentTab !== this.state.tabs[0]) {
                    this.updateTab(this.state.tabs[currentIndex - 1]);
                }
            }
        } else {
            if (e.key === 'ArrowRight') {
                const currentIndex = this.state.tabs.indexOf(this.state.currentTab);
                if (this.state.currentTab !== this.state.tabs[this.state.tabs.length - 1]) {
                    this.updateTab(this.state.tabs[currentIndex + 1]);
                }
            } else if (e.key === 'ArrowLeft') {
                const currentIndex = this.state.tabs.indexOf(this.state.currentTab);
                if (this.state.currentTab !== this.state.tabs[0]) {
                    this.updateTab(this.state.tabs[currentIndex - 1]);
                }
            }
        }
    };

    handleFocus = () => {
        this.setState({ hasFocus: true });
    };

    handleBlur = () => {
        this.setState({ hasFocus: false });
    };

    render() {
        // Always get fresh children so tab content updates when props change
        this.tabArray = this.getChildTabs();
        const currentTab = this.tabArray.filter((tab: JSX.Element) => tab.props.tabName === this.state.currentTab);

        const actions = {
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            onKeyDown: this.state.hasFocus ? this.keydownHandler : undefined,
        };

        return (
            <div>
                <div key="tab-panel" className={`lh-tab-panel${this.props.alignment === TabPanelAlignment.Horizontal ? '-horizontal' : ''}`}>
                    <div className="lh-tab-container" tabIndex={this.props.tabIndex} {...actions}>
                        {this.tabArray.map((tab: JSX.Element, index: number) => {
                            const tabName = tab.props.tabName;
                            const tabSelected = tabName === this.state.currentTab;
                            const click = !tabSelected ? () => this.updateTab(tabName) : undefined;

                            return (
                                <div key={`tab-${index}`} className={`lh-tab ${tabName === this.state.currentTab ? 'selected' : ''}`} onClick={click}>
                                    {tabName}
                                </div>
                            );
                        })}
                    </div>
                    <div key="tab-content" className="lh-tab-content">
                        {currentTab}
                    </div>
                </div>
            </div>
        );
    }
}

interface ITabProps {
    tabName: string;
    disabled?: boolean;
    children?: React.ReactNode;
}

const defaultTabProps = {
    disabled: false,
};

const Tab: React.SFC<ITabProps> = (props: ITabProps) => {
    return <div>{props.children}</div>;
};

Tab.defaultProps = defaultTabProps;

export { TabPanel, Tab };



// WEBPACK FOOTER //
// ./src/components/controls/Tab.tsx